import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Form, Input, Space, Table, notification } from 'antd';
import React, { useMemo, useState } from 'react';

import { useGetMediaTags } from '../../../../utils/api/medias/useGetMediaTags';
import { useCreateMediaTag } from '../../../../utils/api/medias/useCreateMediaTag';
import { useUpdateMediaTag } from '../../../../utils/api/medias/useUpdateMediaTag';
import { useDeleteMediaTag } from '../../../../utils/api/medias/useDeleteMediaTag';

const TagDrawer = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [order, setOrder] = useState({});

  const orderBy = useMemo(() => {
    if (order.name === 'ascend') return 'name';
    if (order.name === 'descend') return '-name';
    if (order.medias_count === 'ascend') {
      return 'medias_count';
    }
    if (order.medias_count === 'descend') {
      return '-medias_count';
    }
    if (order.validated_medias_count === 'ascend') {
      return 'validated_medias_count';
    }
    if (order.validated_medias_count === 'descend') {
      return '-validated_medias_count';
    }
    return undefined;
  }, [order]);

  const onChangeOrder = (_pagination, _filters, sorter) => {
    const newOrder = {};

    if (sorter.field === 'name') {
      newOrder['name'] = sorter.order;
    }
    if (sorter.field === 'medias_count') {
      newOrder['medias_count'] = sorter.order;
    }
    if (sorter.field === 'validated_medias_count') {
      newOrder['validated_medias_count'] = sorter.order;
    }

    setOrder(newOrder);
  };

  const { tags, count } = useGetMediaTags({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    search: searchText,
    ordering: orderBy,
  });

  const { createMediaTag } = useCreateMediaTag();
  const { updateMediaTag } = useUpdateMediaTag();
  const { deleteMediaTag } = useDeleteMediaTag();

  const handleAddTag = async () => {
    try {
      const values = await form.validateFields();
      await createMediaTag({ name: values.tagName });

      notification.success({
        message: 'Tag créé',
        description: `« ${values.tagName} » a été créé avec succès.`,
        placement: 'bottomRight',
        duration: 3,
      });
      form.resetFields();
      setIsAddingTag(false);
    } catch (e) {
      notification.error({
        message: 'Erreur de création du tag',
        description: `Le tag n'a pas pu être créé : ${e}`,
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleEditTag = (tag) => {
    setEditingTagId(tag.slug);
    form.setFieldsValue({ tagName: tag.name });
    setIsAddingTag(true);
  };

  const handleUpdateTag = async () => {
    try {
      const values = await form.validateFields();
      await updateMediaTag({
        slug: editingTagId,
        body: { name: values.tagName },
      });

      notification.success({
        message: 'Tag modifié',
        description: 'Les modifications ont été enregistrées.',
        placement: 'bottomRight',
        duration: 3,
      });
      form.resetFields();
      setIsAddingTag(false);
      setEditingTagId(null);
    } catch (e) {
      notification.error({
        message: 'Erreur de modification du tag',
        description: `Les modifications n'ont pu être enregistrées : ${e}`,
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await deleteMediaTag(tagId);

      notification.success({
        message: 'Tag supprimé',
        description: 'Le tag a été supprimé.',
        placement: 'bottomRight',
        duration: 3,
      });
    } catch (e) {
      if (
        e.response?.data?.error ===
        'This tag is still in use and cannot be deleted.'
      ) {
        notification.error({
          message: 'Erreur de suppression de tag',
          description: `Le tag n'a pas pu être supprimé car il est actuellement utilisé pour des médias. Veuillez retirer les tags des médias avant de les supprimer.`,
          placement: 'bottomRight',
          duration: 3,
        });
      } else {
        notification.error({
          message: 'Erreur de suppression de tag',
          description: `Le tag n'a pas pu être supprimé : ${e}`,
          placement: 'bottomRight',
          duration: 3,
        });
      }
    }
  };

  const handleCancelForm = () => {
    form.resetFields();
    setIsAddingTag(false);
    setEditingTagId(null);
  };

  const columns = [
    {
      title: 'Nom du tag',
      dataIndex: 'name',
      key: 'name',
      width: '50%',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: order['name'],
    },
    {
      title: 'Médias validés',
      dataIndex: 'validated_medias_count',
      key: 'validated_medias_count',
      width: '15%',
      align: 'center',
      sorter: (a, b) => a.validated_medias_count - b.validated_medias_count,
      render: (count) => <span style={{ fontWeight: 500 }}>{count}</span>,
      sortOrder: order['validated_medias_count'],
    },
    {
      title: 'Médias totaux',
      dataIndex: 'medias_count',
      key: 'medias_count',
      width: '15%',
      align: 'center',
      sorter: (a, b) => a.medias_count - b.medias_count,
      render: (count) => <span style={{ fontWeight: 500 }}>{count}</span>,
      sortOrder: order['medias_count'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            aria-label="Modifier le tag"
            icon={<EditOutlined />}
            onClick={() => handleEditTag(record)}
            style={{ padding: 0, height: 'auto' }}
          />
          <Button
            type="link"
            aria-label="Supprimer le tag"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteTag(record.slug)}
            style={{ padding: 0, height: 'auto' }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      title="Gestion des tags"
      placement="right"
      onClose={onClose}
      open={open}
      width="calc(100vw - 220px)"
      styles={{
        body: {
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: '16px',
        }}
      >
        <Input
          placeholder="Rechercher un tag..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Table
            dataSource={tags}
            columns={columns}
            rowKey="id"
            size="small"
            scroll={{ y: 'calc(100vh - 300px)' }}
            pagination={{
              size: 'small',
              current: currentPage,
              pageSize: pageSize,
              total: count,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${count} tags`,
              defaultPageSize: pageSize,
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrentPage(1); // Reset to first page when changing page size
              },
            }}
            onChange={onChangeOrder}
          />
        </div>

        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            paddingTop: '16px',
            marginTop: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: '16px',
          }}
        >
          <div style={{ flex: 1 }}>
            {isAddingTag && (
              <Form form={form} layout="vertical">
                <Form.Item
                  name="tagName"
                  rules={[
                    { required: true, message: 'Le nom du tag est requis' },
                    {
                      min: 2,
                      message: 'Le nom doit contenir au moins 2 caractères',
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Ajoutez le nom du tag" />
                </Form.Item>
              </Form>
            )}
          </div>
          <Space>
            {isAddingTag ? (
              <>
                <Button onClick={handleCancelForm}>Annuler</Button>
                <Button
                  type="primary"
                  onClick={editingTagId ? handleUpdateTag : handleAddTag}
                >
                  {editingTagId ? 'Confirmer' : 'Enregistrer'}
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsAddingTag(true)}
              >
                Ajouter un tag
              </Button>
            )}
          </Space>
        </div>
      </div>
    </Drawer>
  );
};

export default TagDrawer;
